import { useMutation, useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import React from "react";
import {
  arrInvalid,
  formatTime,
  handleError,
  reducerFn,
} from "../../../helper";
import {
  ActionList,
  Button,
  DataTable,
  Modal,
  Popover,
  Stack,
  TextStyle,
} from "@shopify/polaris";
import { get } from "lodash";
import SelectRow, { useSelectRowsCtx } from "../../shared/SelectRow";
import { useToastContext } from "../../shared/ToastContext";
import SkeletonPagePolaris from "../../shared/SkeletonPagePolaris";
import EmptyStatePolaris from "../../shared/EmptyStatePolaris";
import styled from "styled-components";
import PaginationPolaris from "../../shared/PaginationPolaris";

const GET_TIKTOK_PRODUCT_STORES = gql`
  query getTiktokProductStores($filter: GetTiktokProductStoreInput!) {
    getTiktokProductStores(filter: $filter) {
      total
      nodes {
        id
        crawlProductID
        mbProductID
        lastRunAt
        status
        errorMessage
        tiktokProductID
      }
    }
  }
`;

const TIKTOK_REPUSH_PRODUCT_BY_ITEM = gql`
  mutation tiktokRePushProductByItem($itemIds: [ID!]!, $pushId: ID!) {
    tiktokRePushProductByItem(itemIds: $itemIds, pushId: $pushId)
  }
`;

export default function TiktokProductStore({ push, open, onClose }) {
  const [filter, setFilter] = React.useReducer(reducerFn, {
    limit: 50,
    paged: 1,
    search: "",
    status: null,
    tiktokPushID: push?.id,
  });
  const [popoverActive, setPopoverActive] = React.useState(true);

  const { data, loading, error, refetch } = useQuery(
    GET_TIKTOK_PRODUCT_STORES,
    {
      variables: {
        filter: {
          ...filter,
          tiktokPushID: push.id
        },
      },
      skip: !push || !push.id || !open,
    },
  );

  const handleStatus = React.useCallback(
    (status) => () => {
      setFilter({ status, limit: 50, paged: 1 });
      setPopoverActive(false);
    },
    [],
  );
  const togglePopoverActive = React.useCallback(
    () => setPopoverActive((popoverActive) => !popoverActive),
    [],
  );

  const handlePaginationChange = React.useCallback((offset, limit) => {
    setFilter({ limit, paged: offset / limit + 1 });
  }, []);

  React.useEffect(() => {
    if (!open) {
      setFilter({
        limit: 50,
        paged: 1,
        search: "",
        status: null,
        tiktokPushID: push?.id,
      });
    }

    return () => {
      setFilter({
        limit: 50,
        paged: 1,
        search: "",
        status: null,
        tiktokPushID: push?.id,
      });
    };
  }, [open]);

  const activator = (
    <Button onClick={togglePopoverActive} disclosure>
      Status
    </Button>
  );

  const nodes = data?.getTiktokProductStores?.nodes || [];

  const isMBProduct = get(push, "source") === "mb_products";

  let { limit, paged } = filter;
  const total = get(data, "getTiktokProductStores.total", 0);
  const totalPage = Math.ceil(total / limit);
  const offset = (paged - 1) * limit;

  const aggregation = {
    page: paged,
    totalPage,
    offset,
    limit,
    onChange: handlePaginationChange,
    total,
  };

  return (
    <Modal
      large
      onClose={onClose}
      open={open}
      title={`Tiktok Push Detail ${push.id ? "#" + push.id : ""}`}
      sectioned
    >
      <>
        <Stack vertical spacing="none">
          <Stack.Item fill>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Popover
                active={popoverActive}
                onClose={togglePopoverActive}
                sectioned
                activator={activator}
              >
                <ActionList
                  actionRole="menuitem"
                  items={[
                    { content: "Pending", onAction: handleStatus("Pending") },
                    { content: "Error", onAction: handleStatus("Error") },
                    { content: "Pushed", onAction: handleStatus("Pushed") },
                  ]}
                />
              </Popover>
            </div>
          </Stack.Item>
          {loading ? (
            <SkeletonPagePolaris sizeSmall />
          ) : error ? (
            <div>Error: {handleError(error?.toString())}</div>
          ) : nodes?.length > 0 ? (
            <>
              <Container>
                <SelectRow
                  data={nodes}
                  refetch={refetch}
                  actionMarkup={<BulkAction />}
                  pushId={push?.id}
                >
                  {({ checkAllMarkup, checkItemMarkup }) => (
                    <>
                      <DataTable
                        headings={[
                          checkAllMarkup,
                          "ID",
                          "Status",
                          isMBProduct ? "MB Product ID" : "Product Crawl ID",
                          "Tiktok Product ID",
                          "Message",
                          "Last Run At",
                        ]}
                        columnContentTypes={Array.from({ length: 6 }).fill(
                          "text",
                        )}
                        verticalAlign="middle"
                        rows={
                          getRows(checkItemMarkup, nodes, isMBProduct) || []
                        }
                        hideScrollIndicator
                      />
                    </>
                  )}
                </SelectRow>
              </Container>
              <PaginationContainer>
                <PaginationPolaris aggregation={aggregation} showTotal />
              </PaginationContainer>
            </>
          ) : (
            <EmptyStatePolaris slim />
          )}
        </Stack>
      </>
    </Modal>
  );
}

function getRows(checkItemMarkup, nodes, isMBProduct) {
  const res = [];
  for (let node of nodes) {
    if (!node || !node.id) continue;
    const {
      id,
      status,
      tiktokProductID,
      errorMessage,
      lastRunAt,
      crawlProductID,
      mbProductID,
    } = node;

    let productID = isMBProduct ? mbProductID : crawlProductID;

    res.push([
      checkItemMarkup(id),
      <div className="id">
        <span>{id}</span>
      </div>,
      <div className="status">
        <span>{status}</span>
      </div>,
      <div className="product-id">
        <span>{productID}</span>
      </div>,
      <div className="tiktok-proudct-id">
        <span>{tiktokProductID}</span>
      </div>,
      <div className="error-message">
        <span>{errorMessage}</span>
      </div>,
      <div className="last-run-at">
        <span>{formatTime(lastRunAt)}</span>
      </div>,
    ]);
  }

  return res;
}

function BulkAction() {
  const [state, setState] = React.useReducer(reducerFn, {
    open: false,
    action: null,
  });

  const { ids, onCompleted, pushId } = useSelectRowsCtx();

  const toggleShowModal = React.useCallback((action) => {
    setState((prev) => ({ ...prev, open: !prev.open, action }));
  }, []);

  const propActions = {
    open: state.open,
    onClose: toggleShowModal,
    onCompleted,
    idsChecked: ids,
    pushId,
  };

  const actionsMarkup = {
    rePush: <BulkActionRePush {...propActions} />,
  };

  return (
    <>
      <Button
        children="Re-push product"
        onClick={() => toggleShowModal("rePush")}
      />

      {actionsMarkup[state.action]}
    </>
  );
}

function BulkActionRePush({ open, onClose, idsChecked, pushId, onCompleted }) {
  const { setNotify, toggleToast } = useToastContext();

  const [rePush, { loading }] = useMutation(TIKTOK_REPUSH_PRODUCT_BY_ITEM, {
    onCompleted: () => {
      setNotify({ msg: "Re-push for item successfully", err: false });
      onClose && onClose();
      onCompleted && onCompleted();
    },
    onError: (err) => {
      setNotify({ msg: handleError(err?.toString()), err: true });
    },
  });

  const handleSubmit = React.useCallback(() => {
    if (!pushId || arrInvalid(idsChecked)) return;

    toggleToast && toggleToast(true);
    setNotify && setNotify({ msg: null, err: false });

    rePush({
      variables: {
        pushId,
        itemIds: idsChecked,
      },
    });
  }, [idsChecked, pushId, rePush]);

  return (
    <Modal
      onClose={onClose}
      open={open}
      primaryAction={{
        content: "Submit",
        loading: loading,
        onAction: handleSubmit,
      }}
      title="Re-push product"
      secondaryActions={[{ content: "Cancel", onAction: onClose }]}
      sectioned
    >
      <p>Are you sure to re-push product?</p>
      <TextStyle variation="subdued">
        This action only processes products that are in an Error state.
      </TextStyle>
    </Modal>
  );
}

const Container = styled.div`
  .error-message {
    width: 300px;
    white-space: break-spaces;
    word-break: break-word;
  }
`;

const PaginationContainer = styled.div`
  padding: 1.6rem;
  overflow-x: scroll;
  background: var(--p-surface-subdued, #f4f6f8);
`;
